import type { ReactNode } from 'react';
import { PetIcon } from '../../screens/CarrierCabinet/carrierCabinetShared';
import './request-card-unified.css';

export interface RequestCardUnifiedProps {
  fromCity: string;
  toCity: string;
  requestCode?: string | number | null;
  tone?: 'green' | 'yellow' | 'gray' | 'default';
  preferred?: boolean;
  urgent?: boolean;
  petType?: string;
  petLabel?: string;
  petWeight?: string;
  dateRange?: string;
  serviceLabel?: string;
  serviceIcon?: ReactNode;
  budget?: string;
  price?: string;
  offersCount?: number;
  selectedOfferText?: string;
  timeAgo?: string;
  actionButton?: ReactNode;
  rightState?: ReactNode;
  compact?: boolean;
  onClick?: () => void;
  onRouteClick?: () => void;
  continuousRouteUnderline?: boolean;
}

export function RequestCardUnified({
  fromCity,
  toCity,
  requestCode,
  tone = 'default',
  preferred,
  urgent,
  petType = 'other',
  petLabel,
  petWeight,
  dateRange,
  serviceLabel,
  serviceIcon,
  budget,
  price,
  offersCount,
  selectedOfferText,
  timeAgo,
  actionButton,
  rightState,
  compact = false,
  onClick,
  onRouteClick,
  continuousRouteUnderline = false,
}: RequestCardUnifiedProps) {
  const isActionable = Boolean(onClick);
  const routeName = <>
    <h2>{onRouteClick ? <button className="request-card-unified__route-button" type="button" onClick={onRouteClick}>{fromCity}</button> : fromCity}</h2>
    <img className="request-card-unified__arrow" src="/figma/assets/carrier-card-route-arrow.svg" alt="" aria-hidden="true" />
    <h2>{onRouteClick ? <button className="request-card-unified__route-button" type="button" onClick={onRouteClick}>{toCity}</button> : toCity}</h2>
  </>;

  return (
    <article
      className={[
        'request-card-unified',
        `is-${tone}`,
        compact ? 'is-compact' : '',
        preferred ? 'is-preferred' : '',
        urgent ? 'is-urgent' : '',
        isActionable ? 'is-clickable' : '',
        rightState ? 'has-state' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role={isActionable ? 'button' : undefined}
      tabIndex={isActionable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        isActionable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
    >
      <div className="request-card-unified__content">
        <div className="request-card-unified__data">
          <div className="request-card-unified__route">
            {continuousRouteUnderline ? <span className="request-card-unified__route-name">{routeName}</span> : routeName}
            {requestCode ? (
              <>
                <i className="request-card-unified__dot" />
                <span className="request-card-unified__code">
                  #{String(requestCode).replace(/^#/, '')}
                </span>
              </>
            ) : null}
            {preferred ? (
              <span className="request-card-unified__tag is-preferred-tag">
                На моём маршруте
              </span>
            ) : null}
            {urgent ? (
              <span className="request-card-unified__tag is-urgent-tag">
                Срочно
              </span>
            ) : null}
          </div>

          <div className="request-card-unified__details">
            <div className="request-card-unified__animal">
              <span>
                <PetIcon type={petType} />
                {petLabel}
              </span>
              {petWeight ? (
                <>
                  <i />
                  <span>{petWeight}</span>
                </>
              ) : null}
            </div>

            <div className="request-card-unified__travel">
              {dateRange ? (
                <span>
                  <img
                    src="/figma/assets/carrier-card-calendar.svg"
                    alt=""
                    aria-hidden="true"
                  />
                  {dateRange}
                </span>
              ) : null}

              {serviceLabel ? (
                <>
                  <i />
                  <span>
                    {serviceIcon}
                    {serviceLabel}
                  </span>
                </>
              ) : null}

              {budget ? (
                <>
                  <i />
                  <span>
                    <img
                      src="/figma/assets/carrier-card-coins.svg"
                      alt=""
                      aria-hidden="true"
                    />
                    {budget}
                  </span>
                </>
              ) : null}

              {price ? (
                <>
                  <i />
                  <strong className="request-card-unified__price-value">
                    {price}
                  </strong>
                </>
              ) : null}
            </div>
          </div>
        </div>

        {selectedOfferText || offersCount !== undefined ? (
          <div className="request-card-unified__action">
            <span>
              {selectedOfferText ??
                `${offersCount} ${offersCount === 1 ? 'отклик' : offersCount && offersCount < 5 ? 'отклика' : 'откликов'}`}
            </span>
            <svg
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M1 14L14 1M14 14V1H1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        ) : null}
      </div>

      {rightState ? (
        <div className="request-card-unified__state">{rightState}</div>
      ) : null}

      {actionButton ? (
        <div className="request-card-unified__cta-wrapper">{actionButton}</div>
      ) : null}

      {timeAgo ? <time className="request-card-unified__time">{timeAgo}</time> : null}
    </article>
  );
}
